import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { Button, Modal } from '@vacasa/react-components-lib';
import { PAL_ADMIN_UNIT_ID_MIN, isPalAdminUnitId } from '@reviews/interfaces';
import { useQueueUnitMigrationRollbackMutation } from '../../services';
import './UnitMigrationRollbackModal.scss';

export type UnitMigrationRollbackModalProps = {
    open: boolean;
    onClose: () => void;
};

type ValidationStatus = 'idle' | 'passed' | 'failed';

/** Accepts comma and/or line breaks between IDs; arbitrary spaces around tokens are ignored. */
function parseAdminUnitIdsFromInput(text: string): number[] {
    const trimmed = text.trim();
    if (trimmed === '') {
        return [];
    }

    const tokens = trimmed
        .split(/[\n\r,\t]+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

    const ids: number[] = [];
    for (const token of tokens) {
        if (!/^\d+$/.test(token)) {
            continue;
        }
        const n = parseInt(token, 10);
        if (Number.isFinite(n) && n > 0) {
            ids.push(n);
        }
    }
    return ids;
}

function formatIdsOnePerLineWithComma(ids: number[]): string {
    if (ids.length === 0) {
        return '';
    }
    return ids.map((id) => `${id},`).join('\n');
}

/** Unique PAL-eligible IDs, preserving first-seen order (duplicates skipped, not counted as errors). */
function uniqueEligiblePalAdminUnitIds(parsed: number[]): number[] {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const id of parsed) {
        if (!isPalAdminUnitId(id)) {
            continue;
        }
        if (seen.has(id)) {
            continue;
        }
        seen.add(id);
        out.push(id);
    }
    return out;
}

/**
 * One pass over user tokens: count entries that are not a valid PAL admin_unit_id (bad format, non-positive, or below min).
 * Duplicates of a valid ID do not add errors.
 */
function analyzeRollbackTokens(text: string): { tokenCount: number; errorCount: number; eligibleUniqueOrdered: number[] } {
    const trimmed = text.trim();
    const empty = { tokenCount: 0, errorCount: 0, eligibleUniqueOrdered: [] as number[] };
    if (trimmed === '') {
        return empty;
    }

    const tokens = trimmed
        .split(/[\n\r,\t]+/)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

    let errorCount = 0;
    const seen = new Set<number>();
    const eligibleUniqueOrdered: number[] = [];

    for (const token of tokens) {
        if (!/^\d+$/.test(token)) {
            errorCount++;
            continue;
        }
        const n = parseInt(token, 10);
        if (!Number.isFinite(n) || n <= 0) {
            errorCount++;
            continue;
        }
        if (!isPalAdminUnitId(n)) {
            errorCount++;
            continue;
        }
        if (seen.has(n)) {
            continue;
        }
        seen.add(n);
        eligibleUniqueOrdered.push(n);
    }

    return { tokenCount: tokens.length, errorCount, eligibleUniqueOrdered };
}

export const UnitMigrationRollbackModal: React.FC<UnitMigrationRollbackModalProps> = ({ open, onClose }) => {
    const [rollbackText, setRollbackText] = useState('');
    const [validationStatus, setValidationStatus] = useState<ValidationStatus>('idle');
    const [rollbackMessage, setRollbackMessage] = useState('');
    const [rollbackIsError, setRollbackIsError] = useState(false);
    const [queueUnitMigrationRollback, { isLoading: rollbackLoading }] = useQueueUnitMigrationRollbackMutation();

    const liveAnalysis = useMemo(() => analyzeRollbackTokens(rollbackText), [rollbackText]);
    const eligibleIdsPreview = liveAnalysis.eligibleUniqueOrdered;

    useEffect(() => {
        if (!open) {
            setRollbackText('');
            setValidationStatus('idle');
            setRollbackMessage('');
            setRollbackIsError(false);
        }
    }, [open]);

    const handleClose = () => {
        onClose();
    };

    const resetValidationUi = () => {
        setValidationStatus('idle');
        setRollbackMessage('');
        setRollbackIsError(false);
    };

    const onRollbackTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setRollbackText(event.target.value);
        resetValidationUi();
    };

    const onValidateRollbackList = () => {
        setRollbackMessage('');
        setRollbackIsError(false);

        const { tokenCount, errorCount, eligibleUniqueOrdered } = analyzeRollbackTokens(rollbackText);

        if (tokenCount === 0) {
            setValidationStatus('idle');
            setRollbackIsError(true);
            setRollbackMessage('Enter at least one value (comma- and/or line-separated; digits only for PAL admin_unit_id).');
            return;
        }

        if (errorCount > 0) {
            setValidationStatus('failed');
            setRollbackIsError(true);
            setRollbackMessage(
                `${errorCount} value${errorCount === 1 ? '' : 's'} ${
                    errorCount === 1 ? 'has' : 'have'
                } errors (invalid format or below the PAL minimum of ${PAL_ADMIN_UNIT_ID_MIN.toLocaleString(
                    'en-US'
                )}). Fix or remove them before confirming.`
            );
            return;
        }

        setRollbackText(formatIdsOnePerLineWithComma(eligibleUniqueOrdered));
        setValidationStatus('passed');
        setRollbackIsError(false);
        setRollbackMessage(
            `Ready: ${eligibleUniqueOrdered.length} rollback(s) to queue. Review the list and click Confirm—nothing is sent until then.`
        );
    };

    const onEditRollbackListAgain = () => {
        resetValidationUi();
    };

    const onConfirmRollback = async () => {
        if (validationStatus !== 'passed') {
            return;
        }
        const toSend = uniqueEligiblePalAdminUnitIds(parseAdminUnitIdsFromInput(rollbackText));
        if (toSend.length === 0) {
            setRollbackIsError(true);
            setRollbackMessage('No valid IDs to queue. Run validate and format again.');
            setValidationStatus('idle');
            return;
        }
        try {
            setRollbackIsError(false);
            setRollbackMessage('');
            const result = await queueUnitMigrationRollback({ admin_unit_ids: toSend }).unwrap();
            setRollbackMessage(
                `Rollback queued. Messages: ${result.queued}, duplicates skipped: ${
                    result.duplicates_removed
                }, not new PAL (< ${PAL_ADMIN_UNIT_ID_MIN.toLocaleString('en-US')}): ${result.skipped_below_min_admin_unit_id}, invalid: ${
                    result.skipped_invalid
                }.`
            );
            setRollbackText('');
            setValidationStatus('idle');
        } catch (_e) {
            setRollbackIsError(true);
            setRollbackMessage('Unable to queue rollback. Check the list and try again.');
        }
    };

    const textareaReadOnly = validationStatus === 'passed';
    const canConfirm = validationStatus === 'passed' && eligibleIdsPreview.length > 0;

    return (
        <Modal showModal={open} setShowModal={() => handleClose()} size="medium">
            <div className="unit-migration-rollback-modal">
                <span className="body-small-bold">Unit migration rollback</span>
                <p className="unit-migration-rollback-modal__intro">
                    Paste admin_unit_id values separated by commas and/or line breaks. Click <strong>Validate and format list</strong> to
                    check every value against the PAL rule. If everything is valid, the text is normalized (one ID per line, trailing
                    comma). If any value fails, your list is left unchanged—fix the issues and validate again. Nothing is queued until you
                    click <strong>Confirm</strong>.
                </p>
                <Alert severity="info" sx={{ mt: 0, mb: 1 }}>
                    Rollback must be performed using the new unit's admin_unit_id. New PAL unit IDs are{' '}
                    <strong>{PAL_ADMIN_UNIT_ID_MIN.toLocaleString('en-US')}</strong> or higher; any admin_unit_id below that value is
                    considered a legacy unit ID and will not be sent to the orchestrator.
                </Alert>
                {rollbackMessage && (
                    <Alert severity={rollbackIsError ? 'error' : 'success'} sx={{ mt: 0, mb: 1 }}>
                        {rollbackMessage}
                    </Alert>
                )}
                <textarea
                    className="unit-migration-rollback-modal__textarea"
                    value={rollbackText}
                    onChange={onRollbackTextChange}
                    placeholder="e.g. 12340001, 12340002 or one ID per line"
                    rows={8}
                    spellCheck={false}
                    readOnly={textareaReadOnly}
                />
                {rollbackText.trim() !== '' && (
                    <div className="unit-migration-rollback-modal__stats">
                        <span>
                            Tokens: <strong>{liveAnalysis.tokenCount}</strong> · Unique valid PAL (≥{' '}
                            {PAL_ADMIN_UNIT_ID_MIN.toLocaleString('en-US')}): <strong>{eligibleIdsPreview.length}</strong>
                        </span>
                        {validationStatus === 'passed' && eligibleIdsPreview.length > 0 && (
                            <span className="unit-migration-rollback-modal__stats-confirm">
                                Total to confirm: <strong>{eligibleIdsPreview.length}</strong> rollback(s)
                            </span>
                        )}
                    </div>
                )}
                <div className="unit-migration-rollback-modal__footer">
                    <Button variant="info" onClick={handleClose} disabled={rollbackLoading}>
                        Cancel
                    </Button>
                    <div className="unit-migration-rollback-modal__footer-actions">
                        {validationStatus !== 'passed' ? (
                            <Button variant="secondary" onClick={onValidateRollbackList} disabled={rollbackLoading}>
                                Validate and format list
                            </Button>
                        ) : (
                            <>
                                <Button variant="info" onClick={onEditRollbackListAgain} disabled={rollbackLoading}>
                                    Edit again
                                </Button>
                                <Button variant="secondary" onClick={onConfirmRollback} disabled={rollbackLoading || !canConfirm}>
                                    Confirm
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};
